<template>
  <v-sheet>
    <v-sheet tile class="d-flex-column d-md-flex">
      <v-select
        v-if="!singleStaffMode"
        class="mr-0 mr-md-10"
        v-model="staff"
        :items="staffNames"
        dense
        outlined
        hide-details
        item-value="id"
        item-text="name"
        return-object
        label="Teacher"
        @change="timestamp = Date.now()"
      ></v-select>
      <v-spacer class="d-md-none" />
      <div class="d-flex-column">
        <v-radio-group
          class="mt-5 ml-2 ma-md-0"
          @change="timestamp = Date.now()"
          label="Staff"
          v-model="staffTypeFilter"
          row
        >
          <v-radio label="Teacher" value="teacher"></v-radio>
          <v-radio label="Admin" value="administrator"></v-radio>
          <v-radio label="Both" value="Both"></v-radio>
        </v-radio-group>
        <v-radio-group
          class="mt-0 ml-2 ma-md-0"
          @change="timestamp = Date.now()"
          label="Schedule Type"
          v-model="scheduleTypeFilter"
          row
        >
          <v-radio label="Virtual" value="Virtual"></v-radio>
          <v-radio label="In-Person" value="In-Person"></v-radio>
          <v-radio label="Both" value="Both"></v-radio>
        </v-radio-group>
      </div>
    </v-sheet>
    <v-divider class="ma-5" />
    <v-sheet tile height="54" class="d-flex">
      <v-btn icon @click="$refs.calendar.prev()">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="text-h5 ma-2 ml-lg-10" v-if="$refs.calendar">
        {{ $refs.calendar.title }}
      </div>

      <v-spacer />
      <div style="100px">
        <v-select
          v-model="type"
          :items="types"
          dense
          outlined
          hide-details
          label="Type"
        ></v-select>
      </div>
      <v-btn icon @click="$refs.calendar.next()">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-sheet>

    <v-calendar
      ref="calendar"
      v-model="value"
      :type="type"
      :events="events"
      @change="getEvents"
      @click:event="showEvent"
      :event-color="getEventColor"
      :first-time="minDayTime"
      :interval-height="intervalHeight"
      :key="timestamp"
    />

    <v-menu
      v-model="selectedOpen"
      :close-on-content-click="false"
      :activator="selectedElement"
      offset-x
    >
      <teacher-calendar-event
        v-model="selectedEvent"
        :staff="selectedStaff"
        @close="selectedOpen = false"
        @deleted="eventDeleted"
      />
    </v-menu>
  </v-sheet>
</template>

<script>
import moment from "moment";
import randomColor from "randomcolor";
import { mapActions, mapGetters } from "vuex";
const TeacherCalendarEvent = () =>
  import("@/components/teacher/TeacherCalendarEvent.vue");
import { schedule2Event } from "@/utils/scheduling/scheduleUtils.js";

export default {
  data: () => ({
    events: [],
    timestamp: Date.now(),
    intervalHeight: 100,
    type: "month",
    types: ["month", "week", "day"],
    value: "",
    minDayTime: "06:00",
    staffNames: [],
    singleStaffMode: false,
    staff: "All Teachers",
    scheduleTypeFilter: "In-Person",
    staffTypeFilter: "teacher",
    eventDialog: false,
    schedule: null,
    schedules: [],
    recurringRules: null,
    dtstart: null,
    hasRoleManageSchedule: false,
    selectedEvent: null,
    selectedElement: null,
    selectedOpen: false,
  }),
  computed: {
    ...mapGetters(["navState", "isAppLoading", "currentUser"]),
    showEventDialog: {
      get() {
        return this.eventDialog && this.navState.dialog;
      },
      set(newVal) {
        return (this.eventDialog = newVal);
      },
    },
  },
  components: { TeacherCalendarEvent },
  mounted() {
    this.hasRoleManageSchedule =
      this.currentUser.roles.includes("MANAGESCHEDULE");
    this.colors = randomColor({
      count: 100,
      luminosity: "dark",
      format: "rgba",
      alpha: 1,
    });
  },
  filters: {
    formatTime(time) {
      return moment(time).format("h:mm a");
    },
  },
  methods: {
    ...mapActions([
      "hideDialog",
      "showDialog",
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    getEventColor(event) {
      return event.color;
    },
    getEvents({ start, end }) {
      const params = {
        start_date: moment(start.date).format("YYYY-MM-DD"),
        end_date: moment(end.date).format("YYYY-MM-DD"),
      };
      let events = [];
      let staffSet = new Set();

      this.$axios
        .get("staff/schedules", { params })
        .then((response) => {
          events = response.data.map((e) => {
            this.schedules.push(e); //store all displayed schedules in list

            const staff = {
              id: e.staffId,
              name: `${e.firstName} ${e.lastName}`,
              color: e.color,
            };
            if (!staffSet.has(staff.name)) {
              staffSet.add(staff.name);
            }

            const color = staff.color
              ? staff.color
              : this.colors[staffSet.size % this.colors.length];

            return schedule2Event(e, color);
          });

          this.staffNames = [...staffSet];
          if (this.staffNames) {
            this.staffNames.sort();
            this.staffNames = ["All Teachers"].concat(this.staffNames);
          }

          this.events = this.filterEvents(events);
          console.log("filtered events: ", this.events);

          //find min hours
          if (this.events && this.events.length > 0) {
            let times = this.events.map((e) => moment(e.start).format("HHmm"));
            this.minDayTime = times.reduce((t1, t2) => (t1 < t2 ? t1 : t2));
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    filterEvents(events) {
      return events.filter((e) => {
        // filter by staff name
        let matchStaffName = false;
        if (
          this.staff &&
          (this.staff == "All Teachers" || this.staff == e.staffName)
        ) {
          matchStaffName = true;
        }

        // filter schedule type
        let matchScheduleType = false;
        if (
          this.scheduleTypeFilter == "Both" ||
          this.scheduleTypeFilter == e.scheduleType
        ) {
          matchScheduleType = true;
        }

        // filter staff type
        let matchStaffType = false;
        if (
          this.staffTypeFilter == "Both" ||
          this.staffTypeFilter == e.staffType
        ) {
          matchStaffType = true;
        }

        return matchStaffName && matchStaffType && matchScheduleType;
      });
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;

        setTimeout(() => {
          this.selectedOpen = true;
        }, 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },

    eventDeleted() {
      this.selectedOpen = false;
      //reload calendar
      this.timestamp = Date.now();
    },
  },
};
</script>

<style></style>
